import React, { useState, useRef } from 'react';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  FaUser, FaIdCard, FaMapMarkerAlt, FaPhone, FaImage, FaSave, FaArrowLeft
} from 'react-icons/fa';

const initialForm = {
  name: '', father_name: '', mother_name: '', spouse_name: '',
  full_address: '', village: '', post: '', hobli: '', taluk: '', district: '', pin: '',
  mobile_phone: '', email: '',
  aadhaar_no: '', pan: '', voter_id: '', ration_card_no: '', driving_license_no: '', passport_no: '',
  category: ''
};

export default function RegistrationForm() {
  const [form, setForm] = useState(initialForm);
  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [pinFeedback, setPinFeedback] = useState(null);
  const [availablePlaces, setAvailablePlaces] = useState([]);
  const [success, setSuccess] = useState(null);
  const photoRef = useRef();
  const sigRef = useRef();
  const navigate = useNavigate();

  const handlePinLookup = async (pinValue) => {
    const cleanPin = String(pinValue || '').trim();
    if (!/^\d{6}$/.test(cleanPin)) {
      setPinFeedback(null);
      setAvailablePlaces([]);
      return;
    }

    setPinLoading(true);
    setPinFeedback('Looking up place details...');
    try {
      let data = null;
      // 1. Try backend endpoint
      try {
        const res = await API.get(`/members/pincode/${cleanPin}`);
        if (res.data?.success) {
          data = res.data;
        }
      } catch (backendErr) {
        // 2. Direct India Post API fallback
        try {
          const directRes = await fetch(`https://api.postalpincode.in/pincode/${cleanPin}`);
          const directJson = await directRes.json();
          if (directJson?.[0]?.Status === 'Success' && Array.isArray(directJson[0].PostOffice) && directJson[0].PostOffice.length > 0) {
            const pos = directJson[0].PostOffice;
            const primary = pos[0];
            data = {
              district: primary.District || '',
              taluk: primary.Block && primary.Block !== 'NA' ? primary.Block : primary.District || '',
              post: primary.Name || '',
              village: pos[0].Name || '',
              state: primary.State || '',
              places: [...new Set(pos.map(p => p.Name).filter(Boolean))]
            };
          }
        } catch (directErr) {}
      }

      if (data) {
        setForm(prev => ({
          ...prev,
          district: data.district || prev.district,
          taluk: data.taluk || prev.taluk,
          post: data.post || prev.post,
          village: prev.village || data.village,
          hobli: prev.hobli || (data.taluk ? `${data.taluk} Hobli` : prev.hobli)
        }));
        if (data.places && data.places.length > 1) {
          setAvailablePlaces(data.places);
        } else {
          setAvailablePlaces([]);
        }
        setPinFeedback(`✓ ${data.taluk ? data.taluk + ', ' : ''}${data.district}${data.state ? ' (' + data.state + ')' : ''}`);
        toast.success(`Place detected: ${data.taluk ? data.taluk + ', ' : ''}${data.district}`);
      } else {
        setPinFeedback('No place found for this PIN code');
        setAvailablePlaces([]);
      }
    } catch (err) {
      setPinFeedback(null);
    } finally {
      setPinLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    if (name === 'pin') {
      const clean = value.replace(/\D/g, '').slice(0, 6);
      if (clean.length === 6 && clean !== form.pin) {
        handlePinLookup(clean);
      } else if (clean.length < 6) {
        setPinFeedback(null);
        setAvailablePlaces([]);
      }
    }
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSignature = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSignature(file);
      setSignaturePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.mobile_phone.trim()) return toast.error('Mobile number is required');
    if (!form.aadhaar_no.trim()) return toast.error('Aadhaar number is required');

    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        formData.append(k, v);
      });
      // Admin registration defaults to pending for Super Admin approval
      formData.append('status', 'pending');
      if (photo) formData.append('photo', photo);
      if (signature) formData.append('signature', signature);

      const res = await API.post('/members', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`Member registered! ID: ${res.data.memberId}`);
      setSuccess(res.data);
      setForm(initialForm);
      setPhoto(null); setSignature(null);
      setPhotoPreview(null); setSignaturePreview(null);
      setPinFeedback(null);
      setAvailablePlaces([]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setPhoto(null); setSignature(null);
    setPhotoPreview(null); setSignaturePreview(null);
    setPinFeedback(null);
    setAvailablePlaces([]);
  };

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: 'Poppins, sans-serif' }}>New Member Registration</h1>
          <p style={{ fontSize: 13, color: '#6b7280' }}>Fill in all details to register a new association member</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/registrations')}>
          <FaArrowLeft size={12} /> Back to Registrations
        </button>
      </div>

      <div className="page-content" style={{ maxWidth: 900, margin: '0 auto' }}>
        {success && (
          <div className="alert alert-success" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>Member Registered Successfully!</strong>
              <div>Member ID: <strong style={{ color: '#15803d' }}>{success.memberId}</strong></div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Status: <span className="badge badge-warning" style={{ textTransform: 'capitalize' }}>Pending Super Admin Approval</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={() => setSuccess(null)}>Register Another</button>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/registrations')}>View List</button>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <div>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>Member Application Form</h2>
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Sri Karunasai Medicinal Plantation Association</p>
            </div>
            <span className="badge badge-success">Official Registration</span>
          </div>

          <form onSubmit={handleSubmit} style={{ padding: 28 }}>
            {/* ── Personal Info ── */}
            <div className="form-section-title"><FaUser size={12} /> Personal Details</div>
            <div className="form-grid-2" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="form-input" name="name" value={form.name} onChange={handleChange} placeholder="Member's full name" required />
              </div>
              <div className="form-group">
                <label className="form-label">Father's Name</label>
                <input className="form-input" name="father_name" value={form.father_name} onChange={handleChange} placeholder="Father's full name" />
              </div>
              <div className="form-group">
                <label className="form-label">Mother's Name</label>
                <input className="form-input" name="mother_name" value={form.mother_name} onChange={handleChange} placeholder="Mother's full name" />
              </div>
              <div className="form-group">
                <label className="form-label">Spouse Name</label>
                <input className="form-input" name="spouse_name" value={form.spouse_name} onChange={handleChange} placeholder="Spouse's full name" />
              </div>
            </div>

            <div className="form-grid-2" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Membership Category</label>
                <input
                  type="text"
                  className="form-input"
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  placeholder="Enter membership category"
                />
              </div>
            </div>

            {/* ── Address ── */}
            <div className="form-section-title"><FaMapMarkerAlt size={12} /> Address Details</div>
            
            {/* PIN Code Input at the top of address for instant auto-filling */}
            <div className="form-grid-3" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="form-label" style={{ marginBottom: 4 }}>
                    PIN Code <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>(Auto-fills place)</span>
                  </label>
                  {pinLoading && <span style={{ fontSize: 11, color: '#16a34a' }}>Searching...</span>}
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    name="pin"
                    value={form.pin}
                    onChange={handleChange}
                    placeholder="Enter 6-digit PIN"
                    maxLength={6}
                    style={{ fontWeight: 600, letterSpacing: '0.05em' }}
                  />
                  {pinLoading && (
                    <div className="spinner" style={{ position: 'absolute', right: 10, top: 10, width: 16, height: 16, borderWidth: 2 }} />
                  )}
                </div>
                {pinFeedback && (
                  <div style={{
                    fontSize: 12,
                    marginTop: 4,
                    color: pinFeedback.startsWith('✓') ? '#15803d' : '#ef4444',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}>
                    {pinFeedback}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">District {form.district && <span style={{ color: '#16a34a', fontSize: 11 }}>✓</span>}</label>
                <input className="form-input" name="district" value={form.district} onChange={handleChange} placeholder="District" />
              </div>

              <div className="form-group">
                <label className="form-label">Taluk {form.taluk && <span style={{ color: '#16a34a', fontSize: 11 }}>✓</span>}</label>
                <input className="form-input" name="taluk" value={form.taluk} onChange={handleChange} placeholder="Taluk" />
              </div>
            </div>

            {availablePlaces.length > 1 && (
              <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', marginBottom: 6 }}>
                  Select Post / Village for PIN {form.pin}:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {availablePlaces.map(place => (
                    <button
                      key={place}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, post: place, village: prev.village || place }))}
                      className="btn btn-ghost btn-sm"
                      style={{
                        fontSize: 12,
                        padding: '3px 10px',
                        background: form.post === place ? '#15803d' : 'white',
                        color: form.post === place ? 'white' : '#1f2937',
                        border: '1px solid #86efac',
                        cursor: 'pointer'
                      }}
                    >
                      {place}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Full Address</label>
              <textarea className="form-textarea" name="full_address" value={form.full_address} onChange={handleChange} placeholder="Door no., Street, Area..." rows={2} />
            </div>

            <div className="form-grid-3" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Post Office</label>
                <input className="form-input" name="post" value={form.post} onChange={handleChange} placeholder="Post office" />
              </div>
              <div className="form-group">
                <label className="form-label">Village</label>
                <input className="form-input" name="village" value={form.village} onChange={handleChange} placeholder="Village name" />
              </div>
              <div className="form-group">
                <label className="form-label">Hobli</label>
                <input className="form-input" name="hobli" value={form.hobli} onChange={handleChange} placeholder="Hobli" />
              </div>
            </div>

            {/* ── Contact ── */}
            <div className="form-section-title"><FaPhone size={12} /> Contact Information</div>
            <div className="form-grid-2" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Mobile Phone <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="form-input" name="mobile_phone" value={form.mobile_phone} onChange={handleChange} placeholder="10-digit mobile number" maxLength={10} required />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" name="email" value={form.email} onChange={handleChange} placeholder="email@example.com" />
              </div>
            </div>

            {/* ── Identity Documents ── */}
            <div className="form-section-title"><FaIdCard size={12} /> Identity Documents</div>
            <div className="form-grid-3" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Aadhaar Number <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="form-input" name="aadhaar_no" value={form.aadhaar_no} onChange={handleChange} placeholder="12-digit Aadhaar" maxLength={14} required />
              </div>
              <div className="form-group">
                <label className="form-label">PAN Number</label>
                <input className="form-input" name="pan" value={form.pan} onChange={handleChange} placeholder="10-character PAN" maxLength={10} style={{ textTransform: 'uppercase' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Voter ID</label>
                <input className="form-input" name="voter_id" value={form.voter_id} onChange={handleChange} placeholder="Voter ID" />
              </div>
              <div className="form-group">
                <label className="form-label">Ration Card No</label>
                <input className="form-input" name="ration_card_no" value={form.ration_card_no} onChange={handleChange} placeholder="Ration card number" />
              </div>
              <div className="form-group">
                <label className="form-label">Driving License No</label>
                <input className="form-input" name="driving_license_no" value={form.driving_license_no} onChange={handleChange} placeholder="Driving license number" />
              </div>
              <div className="form-group">
                <label className="form-label">Passport No</label>
                <input className="form-input" name="passport_no" value={form.passport_no} onChange={handleChange} placeholder="Passport number" />
              </div>
            </div>

            {/* ── Uploads ── */}
            <div className="form-section-title"><FaImage size={12} /> Photo & Signature</div>
            <div className="form-grid-2" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Member Photo</label>
                <input type="file" ref={photoRef} onChange={handlePhoto} accept="image/*" style={{ display: 'none' }} />
                <div
                  className={`file-upload-box ${photoPreview ? 'has-file' : ''}`}
                  onClick={() => photoRef.current.click()}
                  style={{ display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' }}
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="Member Upload" className="file-preview" />
                  ) : (
                    <div style={{ fontSize: 32, opacity: 0.4 }}>📷</div>
                  )}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#16a34a' }}>
                      {photo ? photo.name : 'Upload Passport Photo'}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>JPG, PNG up to 5MB</div>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Member Signature</label>
                <input type="file" ref={sigRef} onChange={handleSignature} accept="image/*" style={{ display: 'none' }} />
                <div
                  className={`file-upload-box ${signaturePreview ? 'has-file' : ''}`}
                  onClick={() => sigRef.current.click()}
                  style={{ display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' }}
                >
                  {signaturePreview ? (
                    <img src={signaturePreview} alt="Signature Preview" className="file-preview" />
                  ) : (
                    <div style={{ fontSize: 32, opacity: 0.4 }}>✍️</div>
                  )}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#16a34a' }}>
                      {signature ? signature.name : 'Upload Signature'}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>JPG, PNG up to 5MB</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12, marginTop: 28, paddingTop: 20, borderTop: '1px solid #f3f4f6' }}>
              <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ flex: 1, justifyContent: 'center' }}>
                {loading ? (
                  <><div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Registering Member...</>
                ) : (
                  <><FaSave size={15} /> Register Member</>
                )}
              </button>
              <button type="button" className="btn btn-ghost btn-lg" onClick={handleReset}>
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
